import type { ReactNode } from "react"
import { AlertTriangle, ChevronDown, ChevronRight, Download, SlidersHorizontal } from "lucide-react"
import { VideoConsiderScaleCheckbox } from "@/components/media/video-consider-scale-checkbox"
import { Textarea } from "@/components/ui/primitives"
import type { PrevizDepthVariant } from "@/stores/shot-panel-store"
import { cn } from "@/lib/utils"
import { useTranslation } from "@/hooks/use-translation"
import { animaticArtifactStatusText } from "@/lib/shot-i18n"
import { formatMs } from "@/lib/format-ms"
import {
  VIDEO_REF_MIN_SECONDS,
  animaticSegmentBelowVideoRefMin,
  animaticSegmentExceedsVideoRefMax,
} from "@/lib/video-duration"

function formatAnimaticDurationSec(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0"
  const rounded = Math.round(seconds * 10) / 10
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
}

export type SeedanceAnimaticRefMode = "video_silent" | "video_with_audio" | "audio_only"

export type ShotVideoGenerationExtrasProps = {
  markers: { id: number; mark_ms: number }[]
  supportsAnimaticReference: boolean
  hasAnimaticSegment: boolean
  sendAnimaticRef: boolean
  onSendAnimaticRefChange: (value: boolean) => void
  animaticRefMode: SeedanceAnimaticRefMode
  onAnimaticRefModeChange: (mode: SeedanceAnimaticRefMode) => void
  videoEngineLabel: string
  animaticSegment: {
    id: number
    start_ms?: number
    end_ms?: number
    reference_clip_status?: string
    reference_clip_url?: string | null
    reference_clip_with_audio_status?: string
    reference_clip_with_audio_url?: string | null
    reference_audio_status?: string
    reference_audio_url?: string | null
  } | null
  maxVideoRefSeconds: number
  /** Лимит превиза. Модель шота, если переопределена; иначе тот же потолок, что у аниматика. */
  previzMaxSeconds?: number
  /** Превиз-референс: привязанный превиз шота (флаг взаимоисключается с аниматиком). */
  hasLinkedPreviz: boolean
  sendPrevizRef: boolean
  onSendPrevizRefChange: (value: boolean) => void
  sendPrevizDepth: boolean
  onSendPrevizDepthChange: (value: boolean) => void
  previzDepthVariant: PrevizDepthVariant
  onPrevizDepthVariantChange: (variant: PrevizDepthVariant) => void
  /** Обе карты посчитаны с текущего исходника. Пока ложно — карточки маски нельзя выбрать. */
  previzDepthReady: boolean
  previzDepthStatus: string | null
  previzStatus: string | null
  previzRefTooLong: boolean
  previzDurationSec: number
  scaleApplicable: boolean
  scaleSheetLinkEnabled: boolean
  videoConsiderScale: boolean
  onVideoConsiderScaleChange: (value: boolean) => void
  onOpenScaleSheet: () => void
  propScaleApplicable: boolean
  propScaleSheetLinkEnabled: boolean
  videoConsiderPropScale: boolean
  onVideoConsiderPropScaleChange: (value: boolean) => void
  onOpenPropScaleSheet: () => void
  /** Hard UI: one checkbox when shot has heroes + sized props (total ≥2). */
  mixedScaleApplicable?: boolean
  mixedScaleSheetLinkEnabled?: boolean
  onOpenMixedScaleSheet?: () => void
  /** Some cast members lack A-pose / front figure for the active scale block. */
  scaleSheetFiguresIncomplete?: boolean
  propScaleSheetFiguresIncomplete?: boolean
  mixedScaleSheetFiguresIncomplete?: boolean
  shootingBoardCheckboxEnabled: boolean
  isSeedanceVideo: boolean
  hasShootingBoard: boolean
  useShootingBoard: boolean
  onUseShootingBoardChange: (value: boolean) => void
  isByteplusSeedance: boolean
  seedText: string
  onSeedTextChange: (value: string) => void
  onSeedBlur: () => void
  shotId: number
  videoSystemOpen: boolean
  onVideoSystemOpenChange: (open: boolean) => void
  videoSystem: string
  onVideoSystemChange: (value: string) => void
}

export function ShotVideoGenerationExtras({
  markers,
  supportsAnimaticReference,
  hasAnimaticSegment,
  sendAnimaticRef,
  onSendAnimaticRefChange,
  animaticRefMode,
  onAnimaticRefModeChange,
  videoEngineLabel,
  animaticSegment,
  maxVideoRefSeconds,
  previzMaxSeconds,
  hasLinkedPreviz,
  sendPrevizRef,
  onSendPrevizRefChange,
  sendPrevizDepth,
  onSendPrevizDepthChange,
  previzDepthVariant,
  onPrevizDepthVariantChange,
  previzDepthReady,
  previzDepthStatus,
  previzStatus,
  previzRefTooLong,
  previzDurationSec,
  scaleApplicable,
  scaleSheetLinkEnabled,
  videoConsiderScale,
  onVideoConsiderScaleChange,
  onOpenScaleSheet,
  propScaleApplicable,
  propScaleSheetLinkEnabled,
  videoConsiderPropScale,
  onVideoConsiderPropScaleChange,
  onOpenPropScaleSheet,
  mixedScaleApplicable = false,
  mixedScaleSheetLinkEnabled = false,
  onOpenMixedScaleSheet,
  scaleSheetFiguresIncomplete = false,
  propScaleSheetFiguresIncomplete = false,
  mixedScaleSheetFiguresIncomplete = false,
  shootingBoardCheckboxEnabled,
  isSeedanceVideo,
  hasShootingBoard,
  useShootingBoard,
  onUseShootingBoardChange,
  isByteplusSeedance,
  seedText,
  onSeedTextChange,
  onSeedBlur,
  shotId,
  videoSystemOpen,
  onVideoSystemOpenChange,
  videoSystem,
  onVideoSystemChange,
}: ShotVideoGenerationExtrasProps) {
  const { t } = useTranslation(["shot", "common"])
  const startMs = animaticSegment?.start_ms ?? 0
  const endMs = animaticSegment?.end_ms ?? 0
  const animaticSegmentDurationSec = Math.max(0, (endMs - startMs) / 1000)
  const animaticRefTooLong = Boolean(
    animaticSegment && animaticSegmentExceedsVideoRefMax(startMs, endMs, maxVideoRefSeconds),
  )
  const animaticRefTooShort = Boolean(animaticSegment && animaticSegmentBelowVideoRefMin(startMs, endMs))
  const previzDepthChoiceNote = !previzDepthReady
    ? previzStatus !== "ready"
      ? t("previz.depthNeedsMp4")
      : previzDepthStatus === "pending" || previzDepthStatus === "processing"
        ? t("previz.depthGenerating")
        : t("previz.depthNeedsGenerate")
    : undefined

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">
        <SlidersHorizontal className="h-3 w-3" /> {t("common.generationSettings")}
      </div>
      <div className="space-y-3">
        {markers.length > 0 ? (
          <div className="rounded-lg border border-ui-border bg-ui-surface px-3 py-2">
            <p className="text-[10px] font-medium tracking-wide text-ui-text-muted uppercase">
              {t("common.activeVideoMarkers")}
            </p>
            <ul className="mt-1.5 flex flex-wrap gap-1.5">
              {markers.map((m) => (
                <li
                  key={m.id}
                  className="rounded border border-amber-500/30 bg-amber-950/30 px-1.5 py-0.5 font-mono text-[10px] text-amber-100 tabular-nums"
                >
                  {formatMs(m.mark_ms)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {supportsAnimaticReference ? (
          <div className="rounded-lg border border-ui-border px-3 py-2.5 dark:bg-ui-bg-elevated/50 light:bg-clay-1">
            <label
              className={cn(
                "flex items-start gap-2 text-xs text-ui-text-secondary",
                !hasAnimaticSegment ? "cursor-not-allowed opacity-70" : "cursor-pointer",
              )}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-ui-border-strong bg-ui-surface-sunken disabled:opacity-50"
                checked={sendAnimaticRef && hasAnimaticSegment}
                disabled={!hasAnimaticSegment}
                onChange={(e) => onSendAnimaticRefChange(e.target.checked)}
              />
              <span>
                <span className="font-medium text-ui-text-primary">{t("common.sendAnimaticSegment")}</span>
                {!hasAnimaticSegment ? (
                  <span className="mt-0.5 block text-[10px] leading-snug text-ui-text-muted">
                    {t("common.noAnimaticSegment")}
                  </span>
                ) : animaticRefTooLong || animaticRefTooShort ? (
                  <span className="mt-0.5 flex items-start gap-1 text-[10px] leading-snug text-ui-status-rendering">
                    <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden />
                    <span>
                      {animaticRefTooLong
                        ? t("common.sendAnimaticSegmentTooLong", {
                            duration: formatAnimaticDurationSec(animaticSegmentDurationSec),
                            max: maxVideoRefSeconds,
                          })
                        : t("common.sendAnimaticSegmentTooShort", {
                            duration: formatAnimaticDurationSec(animaticSegmentDurationSec),
                            min: VIDEO_REF_MIN_SECONDS,
                          })}
                    </span>
                  </span>
                ) : (
                  <span className="mt-0.5 block text-[10px] leading-snug text-ui-text-muted">
                    {t("common.sendAnimaticSegmentHint", { max: maxVideoRefSeconds })}
                  </span>
                )}
              </span>
            </label>
            {sendAnimaticRef && hasAnimaticSegment && animaticSegment ? (
              <div className="mt-3 space-y-2">
                <StoryboardSeedanceAnimaticModeRow
                  mode="video_silent"
                  active={animaticRefMode}
                  onSelect={onAnimaticRefModeChange}
                  title={t("animaticRef.videoSilentTitle")}
                  description={t("animaticRef.videoSilentDesc")}
                  status={animaticSegment.reference_clip_status}
                  downloadUrl={animaticSegment.reference_clip_url}
                  downloadName={`animatic-seg-${animaticSegment.id}-seedance-ref.mp4`}
                  downloadLabel={t("animaticRef.downloadMp4")}
                  maxVideoRefSeconds={maxVideoRefSeconds}
                />
                <StoryboardSeedanceAnimaticModeRow
                  mode="video_with_audio"
                  active={animaticRefMode}
                  onSelect={onAnimaticRefModeChange}
                  title={t("animaticRef.videoWithAudioTitle")}
                  description={t("animaticRef.videoWithAudioDesc")}
                  status={animaticSegment.reference_clip_with_audio_status}
                  downloadUrl={animaticSegment.reference_clip_with_audio_url}
                  downloadName={`animatic-seg-${animaticSegment.id}-seedance-ref-with-audio.mp4`}
                  downloadLabel={t("animaticRef.downloadMp4WithAudio")}
                  extraNote={t("animaticRef.videoWithAudioExtra")}
                  maxVideoRefSeconds={maxVideoRefSeconds}
                />
                {videoEngineLabel !== "Kling" ? (
                  <StoryboardSeedanceAnimaticModeRow
                    mode="audio_only"
                    active={animaticRefMode}
                    onSelect={onAnimaticRefModeChange}
                    title={t("animaticRef.audioOnlyTitle")}
                    description={t("animaticRef.audioOnlyDesc")}
                    status={animaticSegment.reference_audio_status}
                    downloadUrl={animaticSegment.reference_audio_url}
                    downloadName={`animatic-seg-${animaticSegment.id}-seedance-ref-audio.mp3`}
                    downloadLabel={t("animaticRef.downloadMp3")}
                    maxVideoRefSeconds={maxVideoRefSeconds}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {supportsAnimaticReference ? (
          <div className="rounded-lg border border-ui-border px-3 py-2.5 dark:bg-ui-bg-elevated/50 light:bg-clay-1">
            <label
              className={cn(
                "flex items-start gap-2 text-xs text-ui-text-secondary",
                !hasLinkedPreviz || previzRefTooLong ? "cursor-not-allowed" : "cursor-pointer",
                !hasLinkedPreviz && "opacity-70",
              )}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-ui-border-strong bg-ui-surface-sunken disabled:opacity-50"
                checked={sendPrevizRef && hasLinkedPreviz && !previzRefTooLong}
                disabled={!hasLinkedPreviz || previzRefTooLong}
                onChange={(e) => onSendPrevizRefChange(e.target.checked)}
              />
              <span>
                <span className="font-medium text-ui-text-primary">{t("previz.send")}</span>
                {!hasLinkedPreviz ? (
                  <span className="mt-0.5 block text-[10px] leading-snug text-ui-text-muted">
                    {t("previz.noLinked")}
                  </span>
                ) : previzRefTooLong ? (
                  <span className="mt-0.5 flex items-start gap-1 text-[10px] leading-snug text-ui-status-rendering">
                    <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden />
                    <span>
                      {t("previz.sendTooLong", {
                        duration: formatAnimaticDurationSec(previzDurationSec),
                        max: previzMaxSeconds ?? maxVideoRefSeconds,
                      })}
                    </span>
                  </span>
                ) : previzStatus === "failed" ? (
                  <span className="mt-0.5 flex items-start gap-1 text-[10px] leading-snug text-ui-status-rendering">
                    <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden />
                    <span>{t("previz.sendTranscodeFailed")}</span>
                  </span>
                ) : previzStatus === "pending" || previzStatus === "processing" ? (
                  <span className="mt-0.5 block text-[10px] leading-snug text-ui-text-muted">
                    {t("previz.sendTranscodePending")}
                  </span>
                ) : (
                  <span className="mt-0.5 block text-[10px] leading-snug text-ui-text-muted">
                    {t("previz.sendHint", { max: previzMaxSeconds ?? maxVideoRefSeconds })}
                  </span>
                )}
              </span>
            </label>
            {sendPrevizRef && hasLinkedPreviz && !previzRefTooLong ? (
              <div className="mt-3 space-y-2">
                <ReferenceChoiceCard
                  selected={!sendPrevizDepth}
                  onSelect={() => onSendPrevizDepthChange(false)}
                  title={t("previz.sendOriginalTitle")}
                  description={t("previz.sendOriginalDesc")}
                />
                <ReferenceChoiceCard
                  selected={sendPrevizDepth && previzDepthVariant !== "vis"}
                  disabled={!previzDepthReady}
                  onSelect={() => {
                    onSendPrevizDepthChange(true)
                    onPrevizDepthVariantChange("gray")
                  }}
                  title={t("previz.sendDepthBwTitle")}
                  description={t("previz.sendDepthBwDesc")}
                  extraNote={previzDepthChoiceNote}
                />
                <ReferenceChoiceCard
                  selected={sendPrevizDepth && previzDepthVariant === "vis"}
                  disabled={!previzDepthReady}
                  onSelect={() => {
                    onSendPrevizDepthChange(true)
                    onPrevizDepthVariantChange("vis")
                  }}
                  title={t("previz.sendDepthColorTitle")}
                  description={t("previz.sendDepthColorDesc")}
                  extraNote={previzDepthChoiceNote}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {mixedScaleApplicable ? (
          <VideoConsiderScaleCheckbox
            presentation="detailed"
            scaleApplicable={mixedScaleApplicable}
            scaleSheetLinkEnabled={mixedScaleSheetLinkEnabled}
            videoConsiderScale={videoConsiderScale}
            onVideoConsiderScaleChange={(value) => {
              onVideoConsiderScaleChange(value)
              onVideoConsiderPropScaleChange(value)
            }}
            onOpenScaleSheet={() => onOpenMixedScaleSheet?.()}
            warning={mixedScaleSheetFiguresIncomplete ? t("common.videoConsiderScaleFiguresIncomplete") : null}
            labels={{
              title: t("common.videoConsiderMixedScale"),
              hintBefore: t("common.videoConsiderMixedScaleHintBefore"),
              hintLink: t("common.videoConsiderMixedScaleHintLink"),
              hintAfter: t("common.videoConsiderMixedScaleHintAfter"),
            }}
          />
        ) : (
          <>
            {scaleApplicable ? (
              <VideoConsiderScaleCheckbox
                presentation="detailed"
                scaleApplicable={scaleApplicable}
                scaleSheetLinkEnabled={scaleSheetLinkEnabled}
                videoConsiderScale={videoConsiderScale}
                onVideoConsiderScaleChange={onVideoConsiderScaleChange}
                onOpenScaleSheet={onOpenScaleSheet}
                warning={scaleSheetFiguresIncomplete ? t("common.videoConsiderScaleFiguresIncomplete") : null}
              />
            ) : null}

            {propScaleApplicable ? (
              <VideoConsiderScaleCheckbox
                presentation="detailed"
                scaleApplicable={propScaleApplicable}
                scaleSheetLinkEnabled={propScaleSheetLinkEnabled}
                videoConsiderScale={videoConsiderPropScale}
                onVideoConsiderScaleChange={onVideoConsiderPropScaleChange}
                onOpenScaleSheet={onOpenPropScaleSheet}
                warning={propScaleSheetFiguresIncomplete ? t("common.videoConsiderScaleFiguresIncomplete") : null}
                labels={{
                  title: t("common.videoConsiderPropScale"),
                  hintBefore: t("common.videoConsiderPropScaleHintBefore"),
                  hintLink: t("common.videoConsiderPropScaleHintLink"),
                  hintAfter: t("common.videoConsiderPropScaleHintAfter"),
                  disabled: t("common.videoConsiderPropScaleDisabled"),
                }}
              />
            ) : null}
          </>
        )}

        <div className="rounded-lg border border-ui-border px-3 py-2.5 dark:bg-ui-bg-elevated/50 light:bg-clay-1">
          <label
            className={cn(
              "flex items-start gap-2 text-xs text-ui-text-secondary",
              shootingBoardCheckboxEnabled ? "cursor-pointer" : "cursor-not-allowed opacity-70",
            )}
          >
            <input
              type="checkbox"
              className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-ui-border-strong bg-ui-surface-sunken disabled:opacity-50"
              checked={shootingBoardCheckboxEnabled && useShootingBoard}
              disabled={!shootingBoardCheckboxEnabled}
              onChange={(e) => onUseShootingBoardChange(e.target.checked)}
            />
            <span>
              <span className="font-medium text-ui-text-primary">{t("common.sendShootingBoardReference")}</span>
              <span className="mt-0.5 block text-[10px] leading-snug text-ui-text-muted">
                {!isSeedanceVideo
                  ? t("common.shootingBoardSeedanceOnly")
                  : hasShootingBoard
                    ? t("common.shootingBoardReferenceHint")
                    : t("common.noShootingBoard")}
              </span>
            </span>
          </label>
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-white/5 bg-ui-surface px-3 py-2 text-[11px] text-ui-text-muted">
          <span className="font-medium text-ui-text-secondary">{t("common.provider")}</span> {videoEngineLabel}
          {isByteplusSeedance ? (
            <>
              <span className="mx-1 h-3 w-px bg-ui-surface-raised" />
              <span className="font-medium text-ui-text-secondary">Seed:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={seedText}
                onChange={(e) => onSeedTextChange(e.target.value.replace(/[^\d]/g, ""))}
                onBlur={onSeedBlur}
                title={t("common.originalSeed", { id: shotId })}
                className="w-20 rounded border border-ui-border bg-ui-surface-sunken px-1.5 py-0.5 font-mono text-[11px] text-ui-text-primary tabular-nums outline-none focus:border-ui-accent/60"
              />
              {seedText.trim() !== "" && seedText.trim() !== String(shotId) ? (
                <span className="text-[10px] text-ui-status-review" title={t("common.originalShort", { id: shotId })}>
                  override
                </span>
              ) : null}
            </>
          ) : null}
        </div>

        <div>
          <button
            type="button"
            onClick={() => onVideoSystemOpenChange(!videoSystemOpen)}
            className="flex items-center gap-1 text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase hover:text-ui-text-secondary"
          >
            {videoSystemOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {t("common.systemPromptVideo")}
          </button>
          {videoSystemOpen ? (
            <Textarea
              value={videoSystem}
              onChange={(e) => onVideoSystemChange(e.target.value)}
              className="mt-1.5 min-h-[120px] font-mono text-xs leading-relaxed"
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

type StoryboardSeedanceAnimaticModeRowProps = {
  mode: SeedanceAnimaticRefMode
  active: SeedanceAnimaticRefMode
  onSelect: (mode: SeedanceAnimaticRefMode) => void
  title: string
  description: string
  status?: string
  downloadUrl?: string | null
  downloadName: string
  downloadLabel: string
  extraNote?: string
  maxVideoRefSeconds: number
}

function StoryboardSeedanceAnimaticModeRow({
  mode,
  active,
  onSelect,
  title,
  description,
  status,
  downloadUrl,
  downloadName,
  downloadLabel,
  extraNote,
  maxVideoRefSeconds,
}: StoryboardSeedanceAnimaticModeRowProps) {
  const selected = active === mode
  const statusText = animaticArtifactStatusText(status, maxVideoRefSeconds)

  return (
    <ReferenceChoiceCard
      selected={selected}
      onSelect={() => onSelect(mode)}
      title={title}
      description={description}
      extraNote={extraNote}
    >
      <p className="mt-1 text-[10px] text-ui-text-muted">{statusText}</p>
      {status === "ready" && downloadUrl ? (
        <a
          href={downloadUrl}
          download={downloadName}
          className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-ui-link hover:text-ui-accent-text"
        >
          <Download className="h-3 w-3 shrink-0" />
          {downloadLabel}
        </a>
      ) : null}
    </ReferenceChoiceCard>
  )
}

type ReferenceChoiceCardProps = {
  selected: boolean
  disabled?: boolean
  onSelect: () => void
  title: string
  description: string
  extraNote?: string
  children?: ReactNode
}

function ReferenceChoiceCard({
  selected,
  disabled = false,
  onSelect,
  title,
  description,
  extraNote,
  children,
}: ReferenceChoiceCardProps) {
  return (
    <label
      className={cn(
        "block rounded-md border px-2.5 py-2 text-xs",
        disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer",
        selected ? "border-ui-accent/60 bg-ui-accent/10" : "border-ui-border bg-ui-surface-sunken",
        !disabled && !selected && "hover:border-ui-border-strong",
      )}
    >
      <div className="flex items-start gap-2">
        <input
          type="radio"
          className="mt-1 h-3 w-3 shrink-0 accent-violet-500 disabled:opacity-50"
          checked={selected}
          disabled={disabled}
          onChange={() => {
            if (!disabled) onSelect()
          }}
        />
        <div className="min-w-0 flex-1">
          <div className="font-medium text-ui-text-primary">{title}</div>
          <p className="mt-0.5 text-[10px] leading-snug text-ui-text-muted">{description}</p>
          {extraNote ? <p className="mt-0.5 text-[10px] leading-snug text-ui-text-muted">{extraNote}</p> : null}
          {children}
        </div>
      </div>
    </label>
  )
}
